import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SellerCustomerDetailView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

export type UpdateCustomerPayload = {
  email?: string | null;
  tags?: string[];
};

type UpdateCustomerInput = UpdateCustomerPayload & {
  storeSlug: string;
  customerId: string;
};

/** Updates a customer's seller-editable fields (email, tags). */
export function useUpdateCustomerMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      storeSlug: _storeSlug,
      customerId,
      ...body
    }: UpdateCustomerInput) =>
      apiJsonClient<SellerCustomerDetailView>(
        endpoints.seller.customerById(customerId),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      ),
    onSuccess: async (data, variables) => {
      queryClient.setQueryData(
        queryKeys.seller.customerDetail(
          variables.storeSlug,
          variables.customerId,
        ),
        data,
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.seller.customersPrefix(variables.storeSlug),
      });
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}
